import { useAuth } from '@elotech/components';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { KeycloakRole, Seletor } from 'roles';
import { getConfigSelector, getUserPermissions } from 'state';

export const usePermissions = () => {
  const { userPermissions, hasRole } = useAuth();
  const { config, seletores } = useSelector(state => ({
    config: getConfigSelector(state),
    seletores: getUserPermissions(state)
  }));

  const hasCloudRole = (role?: KeycloakRole) => {
    return role && hasRole(role);
  };

  const checkRole = useCallback(
    (operation: keyof typeof seletores[number]) =>
      (role?: KeycloakRole, seletor?: Seletor) => {
        if (config.isCloud) {
          return !!role && hasCloudRole(role);
        }
        return !!seletor && !!seletores[seletor]?.[operation];
      },
    [config.isCloud, hasCloudRole, seletores]
  );

  const enableNew = useMemo(() => checkRole('inserir'), [checkRole]);

  const enableView = useCallback(
    (role = '', seletor = '') => {
      if (config.isCloud) {
        return hasRole(role);
      }
      return seletor in seletores;
    },
    [config.isCloud, hasRole, seletores]
  );

  const enableEdit = useCallback(
    (role = '', seletor = '') => {
      if (config.isCloud) {
        return hasRole(role);
      }
      return seletores[seletor]?.alterar;
    },
    [config.isCloud, hasRole, seletores]
  );

  const enableDelete = useCallback(
    (role = '', seletor = '') => {
      if (config.isCloud) {
        return hasRole(role);
      }
      return seletores[seletor]?.excluir;
    },
    [config.isCloud, hasRole, seletores]
  );

  const enableHistory = useCallback(
    (role = '', seletor = '') => {
      if (config.isCloud) {
        return hasRole(role);
      }
      return seletores[seletor]?.inserir;
    },
    [config.isCloud, hasRole, seletores]
  );

  const enableAll = (role?: KeycloakRole, seletor?: Seletor) =>
    enableNew(role, seletor) &&
    enableView(role, seletor) &&
    enableEdit(role, seletor) &&
    enableDelete(role, seletor) &&
    enableHistory(role, seletor);

  const hasSeletor = (seletor?: Seletor) => {
    const isPublic = !seletor && seletores;
    return isPublic || seletores?.hasOwnProperty(seletor);
  };

  const hasPermission = useCallback(
    (role?: KeycloakRole, seletor?: Seletor) => {
      return hasCloudRole(role) || hasSeletor(seletor);
    },
    [hasCloudRole, hasSeletor]
  );

  return {
    enableNew,
    enableView,
    enableEdit,
    enableDelete,
    enableHistory,
    enableAll,
    hasSeletor,
    hasRole: hasCloudRole,
    hasPermission,
    permissions: config.isCloud ? userPermissions.clientRoles : seletores
  };
};
